import notify from '../notify'

/**
 * Safe wrapper around notify that escapes HTML in messages to prevent XSS
 * Always use this instead of direct notify calls when displaying user input
 */

/**
 * Escapes HTML entities in a string to prevent XSS
 */
function escapeHtml(str: string): string {
  const div = document.createElement('div')
  div.textContent = str
  return div.innerHTML
}

/**
 * Safely formats a message with potentially unsafe values
 * @param template - Message template with placeholders
 * @param values - Values to insert (will be HTML-escaped)
 * @example safeFormat('Could not load ${name}', { name: userInput })
 */
function safeFormat(template: string, values: Record<string, string | undefined | null>): string {
  return template.replace(/\${(\w+)}/g, (_, key) => {
    const value = values[key]
    return value ? escapeHtml(value) : ''
  })
}

export const safeNotify = {
  error: (message: string) => {
    notify.error(escapeHtml(message))
  },

  success: (message: string) => {
    notify.success(escapeHtml(message))
  },

  /**
   * Display an error with user input safely escaped
   * @example errorWithInput('Could not load uni: ${uni}', { uni: uniName })
   */
  errorWithInput: (template: string, values: Record<string, string | undefined | null>) => {
    notify.error(safeFormat(template, values))
  },

  /**
   * Display a success message with user input safely escaped
   * @example successWithInput('Created ${entity} successfully', { entity: entityName })
   */
  successWithInput: (template: string, values: Record<string, string | undefined | null>) => {
    notify.success(safeFormat(template, values))
  },

  dismiss: notify.dismiss,
  dismissAll: notify.dismissAll,
}

export default safeNotify
